/**
 * The IntPoint object represents a location in a two-dimensional coordinate system, where x represents the horizontal axis and y represents the vertical axis. Its coordinates are integers.
 * @see flash.geom.Point
 */
export class IntPoint {
    /**
     * The horizontal coordinate of the point.
     */
    public x: number;
    /**
     * The vertical coordinate of the point.
     */
    public y: number;

    /**
     * Creates a new point.
     * @param x The horizontal coordinate.
     * @param y The vertical coordinate.
     */
    constructor(x: number = 0, y: number = 0) {
        this.x = x;
        this.y = y;
    }

    /**
     * Adds the coordinates of another point to the coordinates of this point.
     * @param p The point to be added.
     */
    public add(p: IntPoint): void {
        this.x += p.x;
        this.y += p.y;
    }

    /**
     * Adds the coordinates of another point to the coordinates of this point to create a new point.
     * @param p The point to be added.
     * @return The new point.
     */
    public addNew(p: IntPoint): IntPoint {
        return new IntPoint(this.x + p.x, this.y + p.y);
    }

    /**
     * Determines whether two points are equal. Two points are equal if they have the same x and y values.
     * @param toCompare The point to be compared.
     * @return A value of <code>true</code> if the object is equal to this Point object; <code>false</code> if it is not equal.
     */
    public equals(toCompare: IntPoint): boolean {
        return (this.x == toCompare.x && this.y == toCompare.y);
    }

    /**
     * Returns a string that contains the values of the x and y coordinates. The string has the form "IntPoint(x, y)", so calling the toString() method for a point at 23,42 would return "IntPoint(23, 42)".
     * @return The string representation of the coordinates.
     *
     */
    public toString(): string {
        return "IntPoint(" + this.x + ", " + this.y + ")";
    }

}
